/**
 * Email + password account forms: sign in, create account, forgot, reset.
 *
 * The server answers the enumeration-sensitive routes identically whatever the
 * truth is, so this file must not embellish. "Check your email" is the honest
 * message for every register/forgot outcome, and inventing a friendlier
 * "that address is already taken" here would undo the property the server went
 * to some trouble to hold.
 */
import { esc } from "@trip/shared/engine/time";
import { ico } from "./icons.js";

type Mode = "signin" | "register";

/* the shared .ed-fld treatment — account fields are ordinary text boxes */
const field = (id: string, label: string, type: string, extra = "") => `
  <label class="ed-fld">
    <span>${label}</span>
    <input id="${id}" type="${type}" ${extra}>
  </label>`;

export function accountPanelHTML(): string {
  return `
    <div class="acct">
      <div class="acct-tabs" role="tablist">
        <button class="acct-tab on" data-acct-mode="signin" role="tab">Sign in</button>
        <button class="acct-tab" data-acct-mode="register" role="tab">Create account</button>
      </div>
      <form id="acctform" novalidate>
        <div id="acct-name" style="display:none">${field("acct-name-input", "Your name", "text", 'autocomplete="name" placeholder="What your co-planners will see"')}</div>
        ${field("acct-email", "Email", "email", 'autocomplete="email" required')}
        ${field("acct-password", "Password", "password", 'autocomplete="current-password" required')}
        <div id="acct-hint" style="display:none;font-size:12px;color:var(--mut);margin:-4px 0 10px">At least 10 characters. Length beats punctuation.</div>
        <button class="btn-cta" type="submit" id="acct-submit" style="width:100%;min-height:44px">Sign in</button>
        <div id="acct-msg" style="display:none;margin-top:12px;font-size:13px;line-height:1.5"></div>
        <button type="button" id="acct-forgot"
          style="background:none;border:0;padding:8px 0 0;color:var(--sea);font-size:12.5px;cursor:pointer;text-decoration:underline">
          Forgot your password?
        </button>
      </form>
    </div>`;
}

function say(msg: string, kind: "ok" | "warn" | "info" = "info") {
  const el = document.getElementById("acct-msg");
  if (!el) return;
  el.style.display = "";
  el.innerHTML = msg;
  el.style.color = kind === "warn" ? "var(--warn)" : kind === "ok" ? "var(--ok)" : "var(--mut)";
}

async function postJson(url: string, body: unknown): Promise<{ status: number; data: any }> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  let data: any = {};
  try {
    data = await res.json();
  } catch {
    /* non-JSON error page */
  }
  return { status: res.status, data };
}

/** Wires the panel produced by accountPanelHTML(). */
export function wireAccountPanel(onSignedIn: () => void): void {
  let mode: Mode = "signin";
  const form = document.getElementById("acctform") as HTMLFormElement | null;
  if (!form) return;
  const emailEl = () => document.getElementById("acct-email") as HTMLInputElement;
  const passEl = () => document.getElementById("acct-password") as HTMLInputElement;
  const submit = () => document.getElementById("acct-submit") as HTMLButtonElement;

  for (const tab of document.querySelectorAll<HTMLElement>("[data-acct-mode]")) {
    tab.addEventListener("click", () => {
      mode = tab.dataset.acctMode as Mode;
      for (const t of document.querySelectorAll(".acct-tab")) t.classList.toggle("on", t === tab);
      document.getElementById("acct-name")!.style.display = mode === "register" ? "" : "none";
      document.getElementById("acct-hint")!.style.display = mode === "register" ? "" : "none";
      submit().textContent = mode === "register" ? "Create account" : "Sign in";
      passEl().setAttribute("autocomplete", mode === "register" ? "new-password" : "current-password");
      const msg = document.getElementById("acct-msg")!;
      msg.style.display = "none";
    });
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const email = emailEl().value.trim();
    const password = passEl().value;
    if (!email || !password) return say("Enter your email and password.", "warn");

    submit().disabled = true;
    try {
      if (mode === "register") {
        const name = (document.getElementById("acct-name-input") as HTMLInputElement)?.value.trim() || undefined;
        const { status, data } = await postJson("/auth/register", { email, password, name });
        if (status === 200) {
          say(`Check <b>${esc(email)}</b> for a confirmation link. It may land in Junk.`, "ok");
          form.reset();
        } else {
          say(esc(data.error || "That didn't work. Try again."), "warn");
        }
        return;
      }

      const { status, data } = await postJson("/auth/login", { email, password });
      if (status === 200) return onSignedIn();
      if (status === 403 && data.code === "unverified") {
        // The password was right, so this is the one case where saying more is
        // safe — and a dead end without it.
        say(
          `Confirm your email address first. <button type="button" id="acct-resend" style="background:none;border:0;padding:0;color:var(--sea);font-size:13px;cursor:pointer;text-decoration:underline">Send the link again</button>`,
          "warn"
        );
        document.getElementById("acct-resend")?.addEventListener("click", async () => {
          await postJson("/auth/resend", { email });
          say("Sent — check your inbox.", "ok");
        });
        return;
      }
      say(esc(data.error || "That email and password don't match."), "warn");
    } catch {
      say("Couldn't reach the server. Check your connection.", "warn");
    } finally {
      submit().disabled = false;
    }
  });

  document.getElementById("acct-forgot")!.addEventListener("click", async () => {
    const email = emailEl().value.trim();
    if (!email) return say("Enter your email address first, then choose 'Forgot your password?'.", "warn");
    await postJson("/auth/forgot", { email });
    say(`If <b>${esc(email)}</b> has an account, a reset link is on its way.`, "ok");
  });
}

/**
 * Sign in without leaving the trip. Someone following a shared link is looking
 * at the itinerary — sending them to the home page to sign in loses their place
 * and, on a key link, risks them not finding their way back.
 */
export function openAccountModal(onSignedIn: () => void): void {
  const modalEl = document.getElementById("modal")!;
  modalEl.style.maxWidth = "440px";
  modalEl.innerHTML = `
    <div class="m-head">
      <div class="m-cat">Account</div><h3>Sign in</h3>
      <button class="m-close" data-action="close" aria-label="close">✕</button>
    </div>
    <div class="m-body">
      <p class="share-intro">
        Signing in lets you keep this trip on your account, so you don't need the link next time.
      </p>
      ${accountPanelHTML()}
      <div id="signin" style="margin-top:16px"></div>
    </div>`;
  document.getElementById("modalbg")!.classList.add("open");
  wireAccountPanel(onSignedIn);
  void fillProviderButtons("signin");
}

/**
 * The Microsoft mark, per the "Sign in with Microsoft" branding guidelines:
 * four 10px squares on a 1px gutter, official colors, no restyling. Inline SVG
 * because controls here are SVG (emoji stay content-only).
 */
const MS_LOGO = `<svg width="21" height="21" viewBox="0 0 21 21" aria-hidden="true"><rect x="0" y="0" width="10" height="10" fill="#f25022"/><rect x="11" y="0" width="10" height="10" fill="#7fba00"/><rect x="0" y="11" width="10" height="10" fill="#00a4ef"/><rect x="11" y="11" width="10" height="10" fill="#ffb900"/></svg>`;

/**
 * One provider button. Microsoft gets its official button (white, gray
 * hairline, Segoe UI, logo + "Sign in with Microsoft") — the standard
 * treatment users recognize; anything else falls back to the house style
 * until it has official branding of its own.
 */
export function providerButtonHTML(p: { id: string; label: string }): string {
  if (p.id === "microsoft") {
    return `<button class="btn-ms" type="button" data-action="signin" data-provider="microsoft">${MS_LOGO}<span>Sign in with Microsoft</span></button>`;
  }
  return `<button class="btn-cta" type="button" data-action="signin" data-provider="${esc(p.id)}">Sign in with ${esc(p.label)}</button>`;
}

/**
 * Renders whatever social providers are configured; silent when none are.
 * Always secondary: email + password is the primary way in, and these render
 * under an "or" so they read as optional.
 */
export async function fillProviderButtons(elId: string): Promise<void> {
  try {
    const providers = (await (await fetch("/auth/providers")).json()) as { id: string; label: string }[];
    const el = document.getElementById(elId);
    if (!el || !providers.length) return;
    el.innerHTML = `<div class="acct-or"><span>or</span></div>` + providers.map(providerButtonHTML).join(" ");
  } catch {
    /* offline — leave empty */
  }
}

/* ---------------- avatar menu (top right, every page) ---------------- */

/**
 * The avatar is the one piece of chrome that survives every page render, so it
 * lives in its own fixed container appended to <body> rather than inside #app,
 * whose innerHTML each page rebuilds. Someone on a key link is already
 * identified by the key's label — the avatar says who the *account* is, which
 * is a different thing and worth not conflating.
 */
export async function renderAvatarMenu(tripId?: string): Promise<void> {
  let root = document.getElementById("avatar-root");
  if (!root) {
    root = document.createElement("div");
    root.id = "avatar-root";
    document.body.appendChild(root);
    document.addEventListener("pointerdown", (e) => {
      if (!root!.contains(e.target as Node)) root!.classList.remove("open");
    });
  }

  let user: { name?: string; email?: string } | null = null;
  try {
    ({ user } = (await (await fetch("/api/me")).json()) as { user: { name?: string; email?: string } | null });
  } catch {
    root.innerHTML = ""; // offline — no account actions worth offering
    return;
  }

  const saved = user && tripId ? await isSaved(tripId) : true;
  const who = user ? user.name || user.email || "Account" : null;
  const initial = who ? who.trim().charAt(0).toUpperCase() : "";

  root.innerHTML = `
    <button class="avatar-btn${user ? " signedin" : ""}" data-action="avatar-toggle"
      title="${user ? esc(who!) : "Sign in"}" aria-label="Account menu">${user ? esc(initial) : ico.user}</button>
    <div class="avatar-menu">
      ${
        user
          ? `<div class="avatar-head">
               <div class="avatar-who"><b>${esc(who!)}</b>${user.email && user.email !== who ? `<span>${esc(user.email)}</span>` : ""}</div>
               <button class="avatar-signout-sm" data-action="signout">Sign out</button>
             </div>
             <a class="avatar-item" href="/profile">Profile</a>
             <a class="avatar-item" href="/">My trips</a>
             ${saved ? "" : `<button class="avatar-item" data-action="savetrip">＋ Save this trip to my account</button>`}
             <a class="avatar-cta" href="/new">New trip</a>`
          : `<button class="avatar-item" data-action="account">Sign in / create account</button>`
      }
    </div>`;
}

export function toggleAvatarMenu(): void {
  document.getElementById("avatar-root")?.classList.toggle("open");
}

async function isSaved(tripId: string): Promise<boolean> {
  try {
    const { trips } = (await (await fetch("/api/me/trips")).json()) as { trips: { id: string }[] };
    return trips.some((t) => t.id === tripId);
  } catch {
    return true; // don't offer an action we can't confirm is needed
  }
}

/** Standalone page for the link in a reset email: /reset#<token> */
export function resetPageHTML(): string {
  return `
    <div class="booklist" style="padding:26px 24px;max-width:440px;margin:0 auto">
      <h3>Choose a new password</h3>
      <form id="resetform" style="padding:12px 2px" novalidate>
        ${field("reset-password", "New password", "password", 'autocomplete="new-password" required')}
        <div style="font-size:12px;color:var(--mut);margin:-4px 0 10px">At least 10 characters.</div>
        <button class="btn-cta" type="submit" style="width:100%;min-height:44px">Save password</button>
        <div id="reset-msg" style="display:none;margin-top:12px;font-size:13px"></div>
      </form>
    </div>`;
}

export function wireResetPage(token: string, onDone: () => void): void {
  const form = document.getElementById("resetform") as HTMLFormElement | null;
  if (!form) return;
  const msg = document.getElementById("reset-msg")!;
  const show = (text: string, warn = false) => {
    msg.style.display = "";
    msg.textContent = text;
    msg.style.color = warn ? "var(--warn)" : "var(--ok)";
  };
  if (!token) show("That reset link is incomplete. Request a new one from the sign-in page.", true);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const password = (document.getElementById("reset-password") as HTMLInputElement).value;
    const { status, data } = await postJson("/auth/reset", { token, password });
    if (status === 200) {
      show("Password saved — signing you in…");
      // The reset signs them in, and every older session was dropped.
      setTimeout(onDone, 700);
      return;
    }
    show(data.error || "That link has expired or was already used.", true);
  });
}
